import { useEffect, useState, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useLang, FLAGS } from '@/lib/i18n';
import { LayoutDashboard, Users, Settings, LogOut, ChevronLeft, Menu, Image, FileText, UserCheck, ListChecks, Sun, Moon, KeyRound, Globe, BookOpen, Mail, BarChart3, Database, LifeBuoy, ShieldCheck, FlaskConical, Camera, Languages, Bell } from 'lucide-react';
import api from '@/lib/api';
import PushSubscribeButton from '@/components/PushSubscribeButton';

const ADMIN_PATH = '/flvbybcnhfnjh-gfytkm-8246';

export default function AdminLayout() {
  const { dict, lang, setLang } = useLang();
  const [pendingApps, setPendingApps] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [modBadge, setModBadge] = useState(0);
  const [pendingMedia, setPendingMedia] = useState(0);

  // Admin defaults to Russian; stores separately
  useEffect(() => {
    if (!localStorage.getItem('admin_lang')) {
      localStorage.setItem('public_lang', lang);
    }
    const adminLang = localStorage.getItem('admin_lang') || 'ru';
    localStorage.setItem('admin_lang', adminLang);
    if (lang !== adminLang) setLang(adminLang);
    // eslint-disable-next-line
  }, []);

  const fetchPendingApps = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [apps, support, mod] = await Promise.all([
        api.get('/admin/applications?status=pending', h).catch(() => null),
        api.get('/admin/support?status=new', h).catch(() => null),
        api.get('/admin/moderation/summary', h).catch(() => null),
      ]);
      if (apps) setPendingApps((apps.data.applications || apps.data || []).length);
      if (support) setUnreadTickets(support.data.unread || 0);
      if (mod) {
        setModBadge(
          (mod.data.pendingComments || 0) +
          (mod.data.pendingReports || 0) +
          (mod.data.pendingMedia || 0)   // model-uploaded photos/videos awaiting review
        );
        setPendingMedia(mod.data.pendingMedia || 0);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    fetchPendingApps();
    const interval = setInterval(fetchPendingApps, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingApps]);

  const handleAdminLang = (l) => {
    localStorage.setItem('admin_lang', l);
    setLang(l);
  };
  const s = dict.admin.sidebar;
  const sidebarLinks = [
    { to: `${ADMIN_PATH}/dashboard`, icon: LayoutDashboard, label: s.dashboard },
    { to: `${ADMIN_PATH}/models`, icon: Users, label: s.models },
    { to: `${ADMIN_PATH}/applications`, icon: FileText, label: s.applications, badge: pendingApps },
    { to: `${ADMIN_PATH}/services`, icon: ListChecks, label: s.services },
    { to: `${ADMIN_PATH}/users`, icon: UserCheck, label: s.users },
    { to: `${ADMIN_PATH}/analytics`, icon: BarChart3, label: 'Аналитика' },
    { to: `${ADMIN_PATH}/experiments`, icon: FlaskConical, label: 'A/B тесты' },
    { to: `${ADMIN_PATH}/moderation`, icon: ShieldCheck, label: 'Модерация', badge: modBadge, attention: pendingMedia > 0, attentionCount: pendingMedia },
    { to: `${ADMIN_PATH}/backups`, icon: Database, label: 'Бэкапы' },
    { to: `${ADMIN_PATH}/email`, icon: Mail, label: s.email || 'Email' },
    { to: `${ADMIN_PATH}/translations`, icon: Languages, label: 'Переводы' },
    { to: `${ADMIN_PATH}/page-content`, icon: FileText, label: 'Тексты страниц' },
    { to: `${ADMIN_PATH}/notifications`, icon: Bell, label: 'Уведомления' },
    { to: `${ADMIN_PATH}/support`, icon: LifeBuoy, label: 'Поддержка', badge: unreadTickets },
    { to: `${ADMIN_PATH}/settings`, icon: Settings, label: s.settings },
    { to: `${ADMIN_PATH}/media`, icon: Image, label: s.media },
    { to: `${ADMIN_PATH}/account`, icon: KeyRound, label: s.account },
    { to: `${ADMIN_PATH}/guide`, icon: BookOpen, label: s.guide || 'Guide' },
  ];
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lightMode, setLightMode] = useState(() => localStorage.getItem('admin_theme') === 'light');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) navigate(`${ADMIN_PATH}/login`, { replace: true });
    // Sync admin session across tabs
    const onStorage = (e) => {
      if (e.key === 'access_token' && !e.newValue) navigate(`${ADMIN_PATH}/login`, { replace: true });
      if (e.key === 'access_token' && e.newValue) window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);

  // Close the mobile drawer on every route change — legit setState-in-effect.
  // eslint-disable-next-line
  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    localStorage.setItem('admin_theme', lightMode ? 'light' : 'dark');
  }, [lightMode]);

  const handleLogout = async () => {
    // Revoke the HttpOnly refresh cookie on the server FIRST. Without this the
    // axios interceptor silently re-issues an access_token from the still-valid
    // refresh cookie on the next 401 — i.e. logout never "sticks".
    try { await api.post('/admin/logout'); } catch { /* ignore network errors */ }
    localStorage.removeItem('access_token');
    // Restore public language
    const publicLang = localStorage.getItem('public_lang') || 'pl';
    localStorage.setItem('lang', publicLang);
    // Navigate to the dedicated login route (ADMIN_PATH itself re-mounts the
    // authenticated layout and would just bounce back here).
    navigate(`${ADMIN_PATH}/login`, { replace: true });
  };

  const themeClass = lightMode ? 'admin-light' : 'admin-dark';

  return (
    <div className={`${themeClass} min-h-screen flex`} style={lightMode ? {
      '--admin-bg': '#F8F9FA', '--admin-surface': '#FFFFFF', '--admin-border': '#E5E7EB',
      '--admin-text': '#111827', '--admin-text-muted': '#6B7280', '--admin-text-dim': '#9CA3AF',
      '--admin-sidebar-bg': '#FFFFFF', '--admin-hover': '#F3F4F6', '--admin-active-bg': 'rgba(225,29,72,0.08)',
    } : {
      '--admin-bg': '#09090B', '--admin-surface': '#18181B', '--admin-border': '#27272A',
      '--admin-text': '#FFFFFF', '--admin-text-muted': '#A1A1AA', '--admin-text-dim': '#52525B',
      '--admin-sidebar-bg': '#0C0C0E', '--admin-hover': '#18181B', '--admin-active-bg': 'rgba(225,29,72,0.1)',
    }}>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed md:sticky top-0 left-0 h-screen z-50 flex flex-col transition-all ${
          collapsed ? 'w-16' : 'w-60'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        style={{ background: 'var(--admin-sidebar-bg)', borderRight: '1px solid var(--admin-border)' }}
      >
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--admin-border)' }}>
          {!collapsed && <span className="font-syne font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{s.dashboard}</span>}
          <button
            data-testid="sidebar-collapse"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden md:flex w-8 h-8 items-center justify-center rounded"
            style={{ background: 'var(--admin-hover)', color: 'var(--admin-text-muted)' }}
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <nav className="flex-1 p-2 space-y-1">
          {sidebarLinks.map(link => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`sidebar-${link.label}`}
                className="relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors"
                style={{
                  background: isActive ? 'var(--admin-active-bg)' : 'transparent',
                  color: isActive ? '#E11D48' : 'var(--admin-text-muted)',
                }}
              >
                <link.icon className="w-5 h-5 shrink-0" />
                {/* Collapsed-sidebar attention dot: a pulsing amber camera badge
                    so model-uploaded photos awaiting review never go unnoticed. */}
                {collapsed && link.attention && (
                  <span
                    data-testid="pending-media-dot"
                    className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-amber-400/40 animate-pulse"
                  />
                )}
                {!collapsed && <span className="flex-1">{link.label}</span>}
                {!collapsed && link.attention && (
                  <span
                    data-testid="pending-media-badge"
                    title="Модель загрузила новые фото/видео — требуется проверка"
                    className="inline-flex items-center gap-1 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-bold animate-pulse"
                  >
                    <Camera className="w-3 h-3" />{link.attentionCount}
                  </span>
                )}
                {!collapsed && !link.attention && link.badge > 0 && (
                  <span data-testid="pending-apps-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#E11D48] text-white text-[10px] font-bold flex items-center justify-center">{link.badge}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-2 space-y-1" style={{ borderTop: '1px solid var(--admin-border)' }}>
          {/* Push notifications toggle */}
          <div className={`px-1 py-1 ${collapsed ? 'flex justify-center' : ''}`}>
            <PushSubscribeButton mode="admin" compact={collapsed} />
          </div>
          {/* Theme toggle */}
          <button
            data-testid="admin-theme-toggle"
            onClick={() => setLightMode(!lightMode)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm w-full transition-colors"
            style={{ color: 'var(--admin-text-muted)' }}
          >
            {lightMode ? <Moon className="w-5 h-5 shrink-0" /> : <Sun className="w-5 h-5 shrink-0" />}
            {!collapsed && <span>{lightMode ? s.darkMode : s.lightMode}</span>}
          </button>
          {/* Language selector */}
          <div className="relative group">
            <button data-testid="admin-lang-toggle"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm w-full transition-colors"
              style={{ color: 'var(--admin-text-muted)' }}>
              <Globe className="w-5 h-5 shrink-0" />
              {!collapsed && <span>{lang.toUpperCase()}</span>}
            </button>
            <div className="hidden group-hover:block absolute bottom-full left-0 mb-1 rounded-lg shadow-lg p-1 z-50 min-w-[120px]"
              style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)' }}>
              {['ru', 'en', 'pl', 'ua'].map(l => (
                <button key={l} onClick={() => handleAdminLang(l)} data-testid={`admin-lang-${l}`}
                  className={`flex items-center gap-2 w-full px-3 py-1.5 rounded text-xs transition-colors ${lang === l ? 'text-[#E11D48]' : ''}`}
                  style={{ color: lang === l ? '#E11D48' : 'var(--admin-text-muted)' }}>
                  {FLAGS[l]} {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm w-full transition-colors hover:text-red-500"
            style={{ color: 'var(--admin-text-muted)' }}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{s.logout}</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen" style={{ background: 'var(--admin-bg)', color: 'var(--admin-text)' }}>
        <div className="md:hidden flex items-center gap-3 p-4" style={{ borderBottom: '1px solid var(--admin-border)' }}>
          <button
            data-testid="mobile-sidebar-toggle"
            onClick={() => setMobileOpen(true)}
            className="w-9 h-9 rounded flex items-center justify-center"
            style={{ background: 'var(--admin-surface)', color: 'var(--admin-text)' }}
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-syne font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{s.dashboard}</span>
        </div>
        <div className="p-4 md:p-8">
          <Outlet context={{ lightMode }} />
        </div>
      </main>
    </div>
  );
}
